import {
  type ChangeEvent,
  type InputHTMLAttributes,
  type AnimationEvent as ReactAnimationEvent,
  useEffect,
  useRef,
} from "react";

type LoginCredentialKind = "username" | "password";

export function loginCredentialAttributes(kind: LoginCredentialKind): {
  name: LoginCredentialKind;
  autoComplete: "username" | "current-password";
} {
  return kind === "username"
    ? { name: "username", autoComplete: "username" }
    : { name: "password", autoComplete: "current-password" };
}

export function businessInputNextValue(input: {
  currentValue: string;
  proposedValue: string;
  browserAutofilled: boolean;
}): string {
  return input.browserAutofilled ? input.currentValue : input.proposedValue;
}

export function shouldRestoreBusinessInputDomValue(input: {
  controlledValue: string;
  domValue: string;
}): boolean {
  return input.controlledValue !== input.domValue;
}

export function clearSensitiveDraftCredential<T extends { managerPin: string }>(draft: T): T {
  return { ...draft, managerPin: "" };
}

export function takeSensitiveCredential(holder: { current: string }): string {
  const credential = holder.current;
  holder.current = "";
  return credential;
}

function matchesAutofill(element: HTMLInputElement): boolean {
  for (const selector of [":autofill", ":-webkit-autofill", ":-moz-autofill"]) {
    try {
      if (element.matches(selector)) return true;
    } catch {
      // Browsers reject autofill pseudo selectors they do not implement.
    }
  }
  return false;
}

function restoreControlledValue(element: HTMLInputElement, value: string): void {
  if (element.value !== value) element.value = value;
}

type AutofillSafeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "autoComplete" | "name" | "onChange" | "value"
> & {
  fieldName: string;
  value: string;
  onUserChange: (value: string) => void;
  sensitive?: boolean;
};

/**
 * A controlled business input that deliberately does not share the browser's
 * login-credential namespace. Password managers can mutate DOM values without
 * a React change event, so the component also restores the controlled value
 * whenever a DOM/state mismatch is detected.
 */
export function AutofillSafeInput({
  fieldName,
  value,
  onUserChange,
  sensitive = false,
  className,
  onAnimationStart,
  onBeforeInput,
  onCompositionStart,
  onKeyDown,
  onPaste,
  onFocus,
  ...inputProps
}: AutofillSafeInputProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const userIntentRef = useRef(false);

  useEffect(() => {
    const checkIntegrity = () => {
      const element = inputRef.current;
      if (
        !element ||
        !shouldRestoreBusinessInputDomValue({ controlledValue: value, domValue: element.value })
      )
        return;
      restoreControlledValue(element, value);
    };
    checkIntegrity();
    const interval = window.setInterval(checkIntegrity, 100);
    return () => {
      window.clearInterval(interval);
    };
  }, [value]);

  const markUserIntent = () => {
    userIntentRef.current = true;
  };
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nativeInputType = (event.nativeEvent as InputEvent).inputType ?? "";
    const browserAutofilled =
      matchesAutofill(event.currentTarget) ||
      (!userIntentRef.current &&
        (nativeInputType === "" || nativeInputType === "insertReplacementText"));
    const nextValue = businessInputNextValue({
      currentValue: value,
      proposedValue: event.currentTarget.value,
      browserAutofilled,
    });
    userIntentRef.current = false;
    if (browserAutofilled) {
      restoreControlledValue(event.currentTarget, value);
      return;
    }
    onUserChange(nextValue);
  };
  const handleAnimationStart = (event: ReactAnimationEvent<HTMLInputElement>) => {
    onAnimationStart?.(event);
    if (event.animationName === "business-autofill-detected") {
      restoreControlledValue(event.currentTarget, value);
    }
  };

  return (
    <input
      {...inputProps}
      ref={inputRef}
      className={["business-input", className].filter(Boolean).join(" ")}
      name={`business-${fieldName}`}
      autoComplete={sensitive ? "new-password" : "off"}
      data-form-type="other"
      data-1p-ignore="true"
      data-lpignore="true"
      data-bwignore="true"
      value={value}
      onChange={handleChange}
      onAnimationStart={handleAnimationStart}
      onBeforeInput={(event) => {
        markUserIntent();
        onBeforeInput?.(event);
      }}
      onCompositionStart={(event) => {
        markUserIntent();
        onCompositionStart?.(event);
      }}
      onKeyDown={(event) => {
        markUserIntent();
        onKeyDown?.(event);
      }}
      onPaste={(event) => {
        markUserIntent();
        onPaste?.(event);
      }}
      onFocus={(event) => {
        if (matchesAutofill(event.currentTarget))
          restoreControlledValue(event.currentTarget, value);
        onFocus?.(event);
      }}
    />
  );
}
